import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

// Common irregular words the vowel-group heuristic mishandles (vowel hiatus like
// po-em / li-on, and words whose spelling hides a spoken syllable).
const SYLLABLE_OVERRIDES = {
  poem: 2, poems: 2, poet: 2, poets: 2, lion: 2, lions: 2, being: 2,
  science: 2, sciences: 2, quiet: 2, quietly: 3, giant: 2, giants: 2,
  idea: 3, ideas: 3, area: 3, areas: 3, create: 2, creates: 2, created: 3,
  creating: 3, creative: 3, rhythm: 2, rhythms: 2, fluid: 2, fluids: 2,
  ruin: 2, ruined: 2, cruel: 1, diary: 3, dial: 2, trial: 2, prior: 2,
  riot: 2, diet: 2, real: 1, really: 2, interesting: 4, comfortable: 4,
  vegetable: 4, temperature: 4, squirrel: 2, choir: 1, prayer: 1, layer: 2,
  evening: 3, business: 2, everything: 3, hour: 1, hours: 1, our: 1,
  ours: 1, fire: 1, fires: 1, wire: 1, hire: 1, tire: 1,
};

function countSyllables(word) {
  word = word.toLowerCase().replace(/[^a-z]/g, '');
  if (!word) return 0;
  if (SYLLABLE_OVERRIDES[word] != null) return SYLLABLE_OVERRIDES[word];
  if (word.length <= 3) return 1;
  // strip silent -ed/-es (keeping the preceding consonant) and silent trailing e
  const w = word.replace(/([^laeiouy])(?:es|ed)$/, '$1').replace(/e$/, '');
  let count = (w.match(/[aeiouy]+/g) || []).length;
  // a consonant + le/les ending forms its own syllable (ta-ble, lit-tle, bot-tles)
  if (/[^aeiouy](?:le|les)$/.test(word)) count += 1;
  return Math.max(1, count);
}

const isVowel = (c) => 'aeiouy'.includes(c);

// Split a word into its syllable segments (e.g. "beautiful" -> ["beau","ti","ful"]).
// The number of segments is always reconciled to countSyllables() so the on-screen
// division never contradicts the count shown beside it.
function splitSyllables(orig) {
  const clean = orig.toLowerCase().replace(/[^a-z]/g, '');
  const target = countSyllables(clean);
  if (target <= 1 || clean.length <= 3) return [orig];

  // Peel a trailing silent "e" (but not the "-le" syllable) so it doesn't stand alone.
  let tail = '';
  let core = clean;
  if (/[^aeiouyl]e$/.test(clean)) { tail = 'e'; core = clean.slice(0, -1); }

  const groups = [];
  for (let i = 0; i < core.length; i++) {
    if (isVowel(core[i])) { let j = i; while (j + 1 < core.length && isVowel(core[j + 1])) j++; groups.push([i, j]); i = j; }
  }
  let cuts = [];
  for (let g = 0; g < groups.length - 1; g++) {
    const ve = groups[g][1], nv = groups[g + 1][0], cc = nv - ve - 1;
    let cut;
    if (cc <= 0) cut = nv;                         // hiatus  po|em
    else if (cc === 1) cut = ve + 1;               // V|CV    o|pen
    else cut = ve + 1 + Math.floor(cc / 2);        // VC|CV   win|dow
    cuts.push(cut);
  }
  // Keep a final consonant + "le" together (ta|ble, not tab|le).
  if (/[^aeiouy]le$/.test(clean)) { const cPos = clean.length - 3; cuts = cuts.filter(c => c < cPos); cuts.push(cPos); core = clean; tail = ''; }

  let segs = [];
  let s = 0;
  for (const c of cuts) { if (c > s && c < core.length) { segs.push(core.slice(s, c)); s = c; } }
  segs.push(core.slice(s));
  segs = segs.filter(x => x.length);
  if (tail) segs[segs.length - 1] += tail;

  const splitLongest = () => {
    let idx = 0;
    for (let i = 1; i < segs.length; i++) if (segs[i].length > segs[idx].length) idx = i;
    const seg = segs[idx];
    let p = -1;
    for (let i = 0; i < seg.length - 1; i++) { if (isVowel(seg[i]) && isVowel(seg[i + 1])) { p = i + 1; break; } }
    if (p < 0) { for (let i = 1; i < seg.length - 1; i++) { if (isVowel(seg[i]) && !isVowel(seg[i + 1])) { p = i + 1; break; } } }
    if (p <= 0 || p >= seg.length) p = Math.floor(seg.length / 2);
    segs.splice(idx, 1, seg.slice(0, p), seg.slice(p));
  };
  const mergeShortest = () => {
    let idx = 0;
    for (let i = 1; i < segs.length; i++) if (segs[i].length < segs[idx].length) idx = i;
    if (idx === segs.length - 1) { segs[idx - 1] += segs[idx]; segs.splice(idx, 1); }
    else { segs[idx] += segs[idx + 1]; segs.splice(idx + 1, 1); }
  };
  let guard = 0;
  while (segs.length < target && guard++ < 20) splitLongest();
  guard = 0;
  while (segs.length > target && guard++ < 20) mergeShortest();
  return segs;
}

function getSyllableColor(n) {
  if (n === 1) return { bg: '#dbeafe', color: '#1d4ed8' };
  if (n === 2) return { bg: '#d1fae5', color: '#065f46' };
  if (n === 3) return { bg: '#fef3c7', color: '#92400e' };
  if (n === 4) return { bg: '#fce7f3', color: '#9d174d' };
  return { bg: '#ede9fe', color: '#5b21b6' };
}

const EXAMPLES = ['beautiful', 'extraordinary', 'hello', 'communication', 'butterfly'];

export default function SyllableCounter() {
  const [text, setText] = useState('');
  const [results, setResults] = useState(null);

  const analyze = useCallback((input) => {
    const raw = input.trim();
    if (!raw) { setResults(null); return; }

    const tokens = raw.split(/\s+/);
    const words = tokens
      .map(t => ({ raw: t, clean: t.replace(/[^a-zA-Z]/g, ''), syllables: 0 }))
      .filter(w => w.clean.length > 0)
      .map(w => ({ ...w, syllables: countSyllables(w.clean), parts: splitSyllables(w.clean) }));

    const total = words.reduce((s, w) => s + w.syllables, 0);
    const max = words.reduce((m, w) => w.syllables > m.syllables ? w : m, words[0]);
    const haiku = words.length >= 3;

    setResults({ words, total, max, haiku });
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    setText(val);
    analyze(val);
  }

  function loadExample(ex) {
    setText(ex);
    analyze(ex);
  }

  const isHaiku = results && (() => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length !== 3) return false;
    const counts = lines.map(l =>
      l.trim().split(/\s+/).filter(w => w.replace(/[^a-zA-Z]/g,'').length > 0)
       .reduce((s, w) => s + countSyllables(w.replace(/[^a-zA-Z]/g,'')), 0)
    );
    return counts[0] === 5 && counts[1] === 7 && counts[2] === 5;
  })();

  return (
    <div className="game-page">
      <PageSEO page="syllable-counter" />
      <div className="container" style={{ maxWidth: 750 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔢</span>
          <h1>Syllable Counter</h1>
          <p>Count syllables in any word or sentence instantly — perfect for poetry, haiku, and linguistics.</p>
        </div>

        <div className="solver-card">
          <label className="fw-bold mb-1">Enter a word, sentence, or poem</label>
          <textarea
            className="form-control"
            rows={4}
            value={text}
            onChange={handleChange}
            placeholder="e.g. The cat sat on the mat"
            style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.05rem', borderRadius: 12 }}
            autoFocus
          />
          <div className="mt-2">
            <small className="text-muted fw-bold me-2">Try:</small>
            {EXAMPLES.map(ex => (
              <button
                key={ex} type="button"
                className="btn btn-sm me-1 fw-bold"
                style={{ borderRadius: '50px', background: 'var(--border)', border: 'none',
                         fontFamily: 'Nunito,sans-serif', fontSize: '0.8rem', padding: '0.2rem 0.7rem' }}
                onClick={() => loadExample(ex)}
              >{ex}</button>
            ))}
          </div>
        </div>

        {results && (
          <>
            {/* Summary stats */}
            <div className="row g-3 mb-3">
              {[
                { label: 'Total Syllables', value: results.total, icon: '🔢', color: '#4f46e5' },
                { label: 'Words Analyzed', value: results.words.length, icon: '📝', color: '#0891b2' },
                { label: 'Most Syllables', value: results.max ? `${results.max.clean} (${results.max.syllables})` : '—', icon: '🏆', color: '#ea580c' },
                { label: 'Avg per Word', value: results.words.length ? (results.total / results.words.length).toFixed(1) : '—', icon: '📊', color: '#059669' },
              ].map(s => (
                <div key={s.label} className="col-6 col-md-3">
                  <div className="solver-card text-center py-3 mb-0">
                    <div style={{ fontSize: '1.5rem' }}>{s.icon}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Haiku detector */}
            {isHaiku && (
              <div className="alert mb-3" style={{ background: '#fce7f3', border: '1px solid #f9a8d4', borderRadius: 12, color: '#9d174d' }}>
                🌸 <strong>That's a haiku!</strong> Your text follows the 5-7-5 syllable pattern.
              </div>
            )}

            {/* Word-by-word breakdown */}
            <div className="solver-card">
              <h5 className="fw-bold mb-1">Word-by-Word Breakdown</h5>
              <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                Each word is split into its syllables (separated by ·) with the count below.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {results.words.map((w, i) => {
                  const { bg, color } = getSyllableColor(w.syllables);
                  return (
                    <div key={i} style={{ textAlign: 'center' }}>
                      <div style={{
                        background: bg, color, fontWeight: 700,
                        padding: '0.4rem 0.8rem', borderRadius: 10,
                        fontSize: '0.95rem', fontFamily: 'Nunito, sans-serif',
                      }}>
                        {w.parts.map((p, pi) => (
                          <React.Fragment key={pi}>
                            {pi > 0 && <span style={{ opacity: 0.4, margin: '0 1px' }}>·</span>}
                            {p}
                          </React.Fragment>
                        ))}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>
                        {w.syllables} {w.syllables === 1 ? 'syl' : 'syls'}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                <small className="text-muted fw-bold me-2">Legend:</small>
                {[1,2,3,4,5].map(n => {
                  const { bg, color } = getSyllableColor(n);
                  return (
                    <span key={n} style={{ background: bg, color, borderRadius: 6, padding: '0.15rem 0.5rem',
                                           fontSize: '0.75rem', fontWeight: 700, marginRight: 4 }}>
                      {n}{n === 5 ? '+' : ''} syl
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Haiku helper */}
            <div className="solver-card">
              <h5 className="fw-bold mb-2">🌸 Writing a Haiku?</h5>
              <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                A haiku needs 3 lines: <strong>5 syllables</strong>, <strong>7 syllables</strong>, <strong>5 syllables</strong>. Enter each line on its own line to check.
              </p>
              {text.trim().split('\n').filter(l => l.trim()).map((line, i) => {
                const count = line.trim().split(/\s+/)
                  .filter(w => w.replace(/[^a-zA-Z]/g,'').length > 0)
                  .reduce((s, w) => s + countSyllables(w.replace(/[^a-zA-Z]/g,'')), 0);
                const targets = [5, 7, 5];
                const target = targets[i];
                const ok = target !== undefined && count === target;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: '0.8rem', color: '#6b7280', width: 50 }}>Line {i + 1}</span>
                    <span style={{ flex: 1, fontWeight: 600, fontSize: '0.9rem' }}>{line.trim()}</span>
                    <span style={{
                      background: target !== undefined ? (ok ? '#d1fae5' : '#fee2e2') : '#f3f4f6',
                      color: target !== undefined ? (ok ? '#065f46' : '#991b1b') : '#6b7280',
                      borderRadius: 8, padding: '0.15rem 0.5rem', fontSize: '0.8rem', fontWeight: 700,
                    }}>
                      {count} {target !== undefined ? `/ ${target}` : 'syls'}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <SeoContent game="syllable-counter" />
      </div>
    </div>
  );
}
